import React from "react";
import { X, CheckCircle, FileText, AlertTriangle, ShieldCheck } from "lucide-react";

export default function POSubmitBidModal({
  isOpen,
  onClose,
  submitPo,
  onSubmitBid,
  isSubmitted,
  onExportPDF,
  proposedCharge,
  totalMaterialCost = 0,
  projectedProfit = 0,
  isLowProfit = false,
  isManagerOrAdmin = false,
  managerNote = "",
  setManagerNote = () => {}
}) {
  if (!isOpen || !submitPo) return null;

  const profitColor = projectedProfit > 0.0001 ? "var(--success)" : projectedProfit < -0.0001 ? "var(--danger)" : "var(--warning)";

  const isSubmitDisabled = isSubmitted || 
    !submitPo.items || 
    submitPo.items.length === 0 || 
    (isLowProfit && (!isManagerOrAdmin || !managerNote.trim()));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: "90%", maxWidth: "520px" }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Submit Bid Proposal</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
          <div style={{ padding: "1rem", borderRadius: "6px", backgroundColor: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--border-color)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0.5rem", fontSize: "0.9rem" }}>
              <span style={{ color: "var(--text-secondary)" }}>EST Number:</span>
              <strong style={{ color: "var(--text-primary)" }}>{submitPo.poNumber}</strong>

              <span style={{ color: "var(--text-secondary)" }}>Client Name:</span>
              <span style={{ color: "var(--text-primary)" }}>{submitPo.clientName}</span>

              <span style={{ color: "var(--text-secondary)" }}>Project / Subject:</span>
              <span style={{ color: "var(--text-primary)" }}>{submitPo.title}</span>

              <span style={{ color: "var(--text-secondary)" }}>Total Material Cost:</span>
              <span style={{ color: "var(--text-primary)" }}>
                ${totalMaterialCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>

              <span style={{ color: "var(--text-secondary)" }}>Proposed Charge:</span>
              <strong style={{ color: "var(--primary)" }}>
                ${proposedCharge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>

              <span style={{ color: "var(--text-secondary)" }}>Projected Profit:</span>
              <strong style={{ color: profitColor }}>
                {projectedProfit < 0
                  ? `-$${Math.abs(projectedProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `$${projectedProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </strong>
            </div>
          </div>

          {/* Low/Zero Profit Warning & Manager Approval Section */}
          {isLowProfit && !isSubmitted && (
            <>
              {!isManagerOrAdmin ? (
                <div style={{
                  padding: "0.85rem 1rem",
                  borderRadius: "6px",
                  backgroundColor: "rgba(231, 76, 60, 0.1)",
                  border: "1px solid var(--danger)",
                  color: "var(--danger)",
                  fontSize: "0.85rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "700" }}>
                    <AlertTriangle size={16} />
                    Manager Approval Required
                  </div>
                  <div>
                    This estimate has a projected profit of{" "}
                    <strong>
                      {projectedProfit < 0
                        ? `-$${Math.abs(projectedProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : `$${projectedProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </strong>{" "}
                    (&le; $0.00). Proposals with zero or negative profit require review and approval by a <strong>Manager</strong> or <strong>Admin</strong> before submitting to the client.
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                    Please contact a Manager or Admin to submit this proposal.
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: "0.85rem 1rem",
                  borderRadius: "6px",
                  backgroundColor: "rgba(241, 196, 15, 0.1)",
                  border: "1px solid var(--warning)",
                  color: "var(--warning)",
                  fontSize: "0.85rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "700" }}>
                    <ShieldCheck size={16} />
                    Manager Low-Profit Override
                  </div>
                  <div>
                    This estimate has a projected profit of{" "}
                    <strong>
                      {projectedProfit < 0
                        ? `-$${Math.abs(projectedProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : `$${projectedProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </strong>{" "}
                    (&le; $0.00). As a Manager or Admin, you can authorize submission by providing a justification note below.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", marginTop: "0.25rem" }}>
                    <label style={{ fontSize: "0.8rem", color: "var(--text-primary)", fontWeight: "600" }}>
                      Justification Note (Required) *
                    </label>
                    <textarea
                      rows={3}
                      className="form-input"
                      placeholder="Explain why zero or negative profit is permitted for this proposal..."
                      value={managerNote}
                      onChange={(e) => setManagerNote(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        fontSize: "0.85rem",
                        borderRadius: "4px",
                        backgroundColor: "var(--bg-input)",
                        color: "var(--text-primary)",
                        borderColor: "var(--border-color)",
                        resize: "vertical"
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.5rem" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSubmitBid}
              disabled={isSubmitDisabled}
              title={
                isLowProfit && !isManagerOrAdmin
                  ? "Manager or Admin role required to submit zero/negative profit estimates"
                  : isLowProfit && isManagerOrAdmin && !managerNote.trim()
                  ? "A justification note is required to submit"
                  : undefined
              }
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                backgroundColor: isSubmitDisabled ? "rgba(255, 255, 255, 0.05)" : "var(--primary)",
                color: isSubmitDisabled ? "var(--text-secondary)" : "#000000",
                cursor: isSubmitDisabled ? "not-allowed" : "pointer"
              }}
            >
              <CheckCircle size={16} />
              {isSubmitted
                ? "Submitted to Client"
                : isLowProfit && !isManagerOrAdmin
                ? "Manager Approval Required"
                : isLowProfit && isManagerOrAdmin
                ? "Authorize & Submit as Manager"
                : "Ready to Submit"}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onExportPDF(submitPo)}
              disabled={!submitPo.items || submitPo.items.length === 0}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                backgroundColor: "var(--success)",
                borderColor: "var(--success)",
                color: "#000000"
              }}
            >
              <FileText size={16} />
              Export EST
            </button>
          </div>

          {(!submitPo.items || submitPo.items.length === 0) && (
            <div style={{ color: "var(--danger)", fontSize: "0.8rem", textAlign: "center" }}>
              Cannot submit an EST without line items.
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
